import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listarSesiones } from "../../api";
import "./NutricionHome.css";

export default function NutricionHome() {
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

  const navigate = useNavigate();

  // ✅ Servicios de Nutrición (normalizados)
  const serviciosNutricion = [
    "nutricion",
    "nutricionista",
    "consulta_nutricional",
    "plan_nutricional",
    "seguimiento_nutricional",
  ];

  // 🧭 mismos posibles nombres de id de paciente que en Logopedia/Psicología
  const extractPatientId = (cita) => {
    return (
      cita.patient_id ??
      cita.paciente_id ??
      cita.patientId ??
      cita.pacienteId ??
      cita.user_id ??
      cita.client_id ??
      cita.child_id ??
      cita.patient?.id ??
      cita.paciente?.id ??
      cita.user?.id ??
      cita.client?.id ??
      null
    );
  };

  // ➡️ Ir a /paciente/:id
  const goToPacienteView = useCallback(
    (cita) => {
      const patientId = extractPatientId(cita);

      if (!patientId) {
        console.warn("[NutricionHome] No se encontró patient_id en la cita:", cita);
        alert(
          "No se pudo abrir la ficha porque la cita no trae el ID del paciente.\n\n" +
            "Asegúrate de que listarSesiones incluya 'patient_id' o 'patient.id' en cada cita."
        );
        return;
      }
      navigate(`/paciente/${patientId}`);
    },
    [navigate]
  );

  // Detectar si es tablet/móvil
  useEffect(() => {
    const checkSize = () => setIsMobileOrTablet(window.innerWidth <= 1024);
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  // Cargar sesiones del día (solo nutrición, filtrando en front)
  useEffect(() => {
    const hoy = new Date().toISOString().split("T")[0];
    listarSesiones({ date: hoy })
      .then((res) => {
        if (!res) return;

        console.table(
          (res.items || []).map((i) => ({
            id: i.id,
            servicio: i.service_code,
            especialidad: i.specialty,
          }))
        );

        if (res.ok) {
          const nutricion = (res.items || []).filter((c) => {
            const code = c.service_code?.toLowerCase().trim();
            const esp = c.specialty?.toLowerCase().trim();
            return (
              serviciosNutricion.includes(code) ||
              serviciosNutricion.includes(esp)
            );
          });
          setCitas(nutricion);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Filtro por texto (nombre del paciente)
  const filtradas = citas.filter((c) =>
    (c.paciente || "").toLowerCase().includes(busqueda.toLowerCase())
  );

  // Navegación del carrusel
  const nextCard = () => {
    if (currentIndex < filtradas.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };
  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  return (
    <div className="pl-wrap">
      <h2 className="text-3xl font-bold mb-2 text-[#704c3b]">
        🥒 Citas de Nutrición (Hoy)
      </h2>
      <p className="text-[#7a6a5c] mb-6">
        Aquí puedes ver las sesiones del área de{" "}
        <strong>Nutrición</strong> programadas para hoy.
      </p>

      {/* 🔍 Buscador */}
      <div className="pl-tools">
        <input
          type="search"
          placeholder="Buscar paciente..."
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setCurrentIndex(0);
          }}
        />
        <div className="pl-count">{filtradas.length} citas hoy</div>
      </div>

      {/* 🧾 Contenedor principal */}
      <div
        className={`pl-table-scroll ${isMobileOrTablet ? "mobile-clean" : ""}`}
      >
        {loading ? (
          <div className="pl-empty">Cargando citas...</div>
        ) : filtradas.length === 0 ? (
          <div className="pl-empty">
            No hay citas de nutrición programadas para hoy.
          </div>
        ) : (
          <>
            {/* 🖥️ Tabla en escritorio */}
            {!isMobileOrTablet && (
              <table className="pl-table">
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Hora</th>
                    <th>Servicio</th>
                    <th>Especialista</th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((cita) => {
                    const hora = new Date(
                      cita.start_datetime
                    ).toLocaleTimeString("es-ES", {
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    return (
                      <tr
                        key={cita.id}
                        className="clickable-row"
                        title="Ver ficha del paciente"
                        onClick={() => goToPacienteView(cita)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            goToPacienteView(cita);
                        }}
                      >
                        <td>{cita.paciente}</td>
                        <td>{hora}</td>
                        <td className="capitalize">
                          {cita.service_code?.replace(/_/g, " ") || "-"}
                        </td>
                        <td className="capitalize">
                          {cita.specialty || "Nutrición"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* 📱 Carrusel en móvil/tablet */}
            {isMobileOrTablet && (
              <div className="mobile-carrusel">
                {filtradas.length > 1 && (
                  <button
                    className="arrow left"
                    onClick={prevCard}
                    disabled={currentIndex === 0}
                  >
                    ◀
                  </button>
                )}

                <div className="cards-container">
                  {filtradas.length > 0 && (
                    <div
                      key={filtradas[currentIndex].id}
                      className="card-cita clickable-card"
                      title="Ver ficha del paciente"
                      onClick={() => goToPacienteView(filtradas[currentIndex])}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          goToPacienteView(filtradas[currentIndex]);
                      }}
                    >
                      <h3>{filtradas[currentIndex].paciente}</h3>
                      <p>
                        <strong>Hora:</strong>{" "}
                        {new Date(
                          filtradas[currentIndex].start_datetime
                        ).toLocaleTimeString("es-ES", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                      <p>
                        <strong>Servicio:</strong>{" "}
                        {filtradas[currentIndex].service_code
                          ?.replace(/_/g, " ")
                          ?.toLowerCase() || "-"}
                      </p>
                      <p>
                        <strong>Especialista:</strong>{" "}
                        {filtradas[currentIndex].specialty || "Nutrición"}
                      </p>
                    </div>
                  )}
                </div>

                {filtradas.length > 1 && (
                  <button
                    className="arrow right"
                    onClick={nextCard}
                    disabled={currentIndex === filtradas.length - 1}
                  >
                    ▶
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
